"use client"

import { FormEvent, useState } from "react"
import { captureAnalyticsEvent, captureNewsletterSubscribed } from "@/lib/analytics/posthog-client"
import Button from "@/modules/common/components/button"

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const NewsletterSignup = () => {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle"
  )
  const [message, setMessage] = useState("")

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!emailPattern.test(email)) {
      setStatus("error")
      setMessage("Vnesite veljaven e-poštni naslov.")
      captureAnalyticsEvent("form_error_triggered", {
        field_name: "newsletter_email",
        form_name: "newsletter_signup",
      })
      return
    }

    setStatus("loading")
    setMessage("")

    try {
      const response = await fetch("/api/newsletter-signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, source: "home" }),
      })

      const data = (await response.json()) as { message?: string }

      if (!response.ok) {
        setStatus("error")
        setMessage(data.message || "Prijava trenutno ni uspela. Poskusite znova.")
        captureAnalyticsEvent("form_error_triggered", {
          field_name: "newsletter_email",
          form_name: "newsletter_signup",
        })
        return
      }

      setStatus("success")
      setMessage("Hvala za prijavo na novice.")
      captureNewsletterSubscribed("home", "newsletter_signup")
      setEmail("")
    } catch {
      setStatus("error")
      setMessage("Prišlo je do napake. Poskusite znova čez trenutek.")
    }
  }

  return (
    <section className="content-container py-12 md:py-16">
      <div className="rounded-2xl bg-navy text-white p-6 md:p-10">
        <p className="text-sm md:text-base font-bold tracking-wide text-white/90 uppercase mb-1">
          Za kuharje, ki želijo več
        </p>
        <p className="text-white/90 mt-2 text-base md:text-lg max-w-xl">
          Prijavite se na e-novice in prvi izvedite za akcije, navdih in novosti
          za vašo kuhinjo.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center"
        >
          <label htmlFor="newsletter-email" className="sr-only">
            E-poštni naslov
          </label>
          <input
            id="newsletter-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="vas@email.si"
            className="w-full sm:max-w-md rounded-full border border-white/40 bg-white text-neutral-900 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-gold"
            autoComplete="email"
            required
          />
          <Button
            type="submit"
            variant="secondary"
            disabled={status === "loading"}
            className="!bg-gold !text-neutral-900 hover:!bg-[#e2b63c] disabled:opacity-70"
          >
            {status === "loading" ? "Pošiljam..." : "Prijavi me"}
          </Button>
        </form>
        <p className="mt-3 text-sm text-white/80">
          Brez neželene pošte. Odjava kadarkoli.
        </p>

        {message ? (
          <p
            className={`mt-3 text-sm ${
              status === "success" ? "text-green-200" : "text-rose-200"
            }`}
          >
            {message}
          </p>
        ) : null}
      </div>
    </section>
  )
}

export default NewsletterSignup
